import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "../constants/site";
import { useLanguage } from "../i18n/LanguageContext";

export const Header = () => {
  const { lang, toggleLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const onDark = !scrolled && !open && location.pathname === "/";
  const tone = onDark ? "text-white" : "text-black";
  const toneMuted = onDark ? "text-white/80 hover:text-white" : "text-black/60 hover:text-black";

  const CHRONO24_URL = "https://www.chrono24.ch/search/index.htm?customerId=36784&dosearch=true";

  const navItems = [
    { to: "/", label: t.nav.home, id: "nav-home" },
    { to: "/about", label: t.nav.about, id: "nav-about" },
    { to: "/services", label: t.nav.services, id: "nav-services" },
    { to: CHRONO24_URL, label: t.nav.catalog, id: "nav-gallery", external: true },
    { to: "/contact", label: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open
          ? "bg-white/95 backdrop-blur-md border-b border-black/10"
          : onDark
          ? "bg-gradient-to-b from-black/60 to-transparent"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 flex items-center justify-between h-20 md:h-24">
        {/* Logo */}
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="AW" className="h-10 md:h-12 w-auto" />
          <div className="hidden sm:flex flex-col leading-none">
            <span className={`font-serif-aw text-lg md:text-xl ${tone}`}>Alpha Watch</span>
            <span className={`font-sans-aw text-[10px] tracking-luxury uppercase mt-1 ${onDark ? "text-white/70" : "text-black/60"}`}>
              Kristian Y. · Zürich
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-10">
          {navItems.map((item) =>
            item.external ? (
              <a
                key={item.id}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={item.id}
                className={`font-sans-aw text-[11px] tracking-luxury uppercase transition-colors duration-300 link-aw ${toneMuted}`}
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.id}
                className={({ isActive }) =>
                  `font-sans-aw text-[11px] tracking-luxury uppercase transition-colors duration-300 link-aw ${
                    isActive ? tone : toneMuted
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        {/* Lang Toggle + Mobile menu */}
        <div className="flex items-center gap-2 sm:gap-4">
          <button
            data-testid="lang-toggle"
            onClick={toggleLang}
            className={`font-sans-aw text-xs tracking-luxury uppercase transition-colors duration-300 border px-3 h-11 min-w-[44px] ${
              onDark ? "border-white/60 hover:border-white bg-black/20" : "border-black/25 hover:border-black bg-white/60"
            }`}
            aria-label={lang === "de" ? "Switch to English" : "Auf Deutsch wechseln"}
          >
            <span className={lang === "de" ? `${tone} font-medium` : onDark ? "text-white/55" : "text-black/40"}>DE</span>
            <span className={`mx-1 ${onDark ? "text-white/40" : "text-black/30"}`}>/</span>
            <span className={lang === "en" ? `${tone} font-medium` : onDark ? "text-white/55" : "text-black/40"}>EN</span>
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className={`lg:hidden flex items-center justify-center w-11 h-11 border ${
              onDark ? "text-white border-white/60 bg-black/20" : "text-black border-black/25 bg-white/60"
            }`}
            aria-label={open ? "Menü schliessen" : "Menü öffnen"}
            aria-expanded={open}
          >
            {open ? <X size={24} strokeWidth={1.75} /> : <Menu size={24} strokeWidth={1.75} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-white border-b border-black/10 ${
          open ? "max-h-96" : "max-h-0"
        }`}
      >
        <nav className="px-6 py-6 flex flex-col gap-5">
          {navItems.map((item) =>
            item.external ? (
              <a
                key={item.id}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${item.id}-mobile`}
                className="font-sans-aw text-sm tracking-luxury-md uppercase text-black/70 py-1"
              >
                {item.label}
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`${item.id}-mobile`}
                className={({ isActive }) =>
                  `font-sans-aw text-sm tracking-luxury-md uppercase py-1 ${
                    isActive ? "text-black" : "text-black/70"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>
      </div>
    </header>
  );
};
